import { ArrowRight, Bot, Check, ClipboardPaste, Eye, FileSearch, LockKeyhole, Radio, RefreshCw, ScanSearch, Send, Share2, Sparkles, Tag, Workflow } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCount, formatRelative } from "../api";
import { useAuth } from "../auth";
import { buildActivationPrompt, buildCatalogPrompt, catalogProviderName, type CatalogProvider } from "../../shared/catalogPrompt";
import { isProviderId } from "../../shared/providers";
import { buildCategorizePrompt } from "../../shared/tags";
import { Button, CopyButton, ProviderMark } from "../components";
import "./onboarding.css";

type CatalogSource = { id: string; provider: string; name: string; lastSyncedAt: string | null; loopCount: number; capabilities: string[] };
type CatalogLoop = { id: string; name: string; cadence: string; kind: string | null; confidence: string | null; lastCatalogedAt: string; source: { id: string; name: string; provider: string } };
type CatalogOverview = { sources: CatalogSource[]; loops: CatalogLoop[]; totals: { sources: number; loops: number } };
type FreshKey = { token: string };

const providers: Array<{ id: CatalogProvider; label: string; detail: string }> = [
  { id: "codex", label: "Codex", detail: "Tasks, automations, repos" },
  { id: "claude", label: "Claude", detail: "Projects, skills, routines" },
  { id: "grok", label: "Grok", detail: "Grok Bot, conversations, workflows" },
  { id: "muse", label: "Muse", detail: "Standing tasks and connectors" },
  { id: "dots", label: "dots", detail: "Goals, plugins, schedules" },
  { id: "other", label: "Another agent", detail: "Any agent that can call MCP or HTTPS" },
];

function extractJson(value: string): unknown {
  const trimmed = value.trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/iu);
  return JSON.parse(fenced?.[1]?.trim() ?? trimmed) as unknown;
}

export function OnboardingPage() {
  const { personalOrganization, setActiveOrganizationId } = useAuth();
  const [provider, setProvider] = useState<CatalogProvider>("codex");
  const [generated, setGenerated] = useState<{ provider: CatalogProvider; prompt: string } | null>(null);
  const [catalog, setCatalog] = useState<CatalogOverview>({ sources: [], loops: [], totals: { sources: 0, loops: 0 } });
  const [generating, setGenerating] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);
  const [categorizeOpen, setCategorizeOpen] = useState(false);
  const [manualJson, setManualJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const canManage = ["owner", "admin"].includes(personalOrganization?.role ?? "");

  const load = useCallback(async () => {
    if (!personalOrganization) {
      setCatalogLoading(false);
      return;
    }
    try {
      const catalogResult = await api<CatalogOverview>("/api/app/catalog", {}, personalOrganization.id);
      setCatalog(catalogResult);
      setLoadError("");
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : "The catalog state could not be read");
    } finally {
      setCatalogLoading(false);
    }
  }, [personalOrganization]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (personalOrganization) setActiveOrganizationId(personalOrganization.id);
  }, [personalOrganization, setActiveOrganizationId]);
  useEffect(() => {
    if (!generated) return;
    const timer = window.setInterval(() => void load(), 3_000);
    return () => window.clearInterval(timer);
  }, [generated, load]);

  const providerSource = catalog.sources.find((source) => source.provider === provider);
  const selected = providers.find((item) => item.id === provider)!;
  const activationSource = providerSource ?? catalog.sources[0];
  const activationProvider = isProviderId(activationSource?.provider) ? activationSource.provider : "other";
  const activationPrompt = buildActivationPrompt(activationProvider);
  const categorizePrompt = buildCategorizePrompt(activationProvider);
  const latestLoops = catalog.loops.slice(0, 8);
  const progress = catalog.totals.sources ? 3 : generated ? 2 : 1;
  const promptMatchesSelection = generated?.provider === provider;
  const journeyState = catalogLoading ? {
    label: "Reading catalog",
    tone: "loading",
    happened: "Signed-in workspace opened",
    pending: "Source and sharing state not confirmed",
    next: "Wait for the catalog response",
  } : loadError ? {
    label: "Catalog unavailable",
    tone: "failure",
    happened: "No new action was applied",
    pending: "Current source state is unconfirmed",
    next: "Retry the catalog read",
  } : catalog.totals.sources ? {
    label: "Catalog records present",
    tone: "ready",
    happened: `${formatCount(catalog.totals.sources, "source")} synced · ${formatCount(catalog.totals.loops, "loop")} mapped`,
    pending: "Observer and per-loop reporting state is not asserted by catalog data",
    next: "Keep catalog-only or ask the agent to preview activation changes",
  } : generated ? {
    label: "Receiver listening",
    tone: "waiting",
    happened: "A scoped scan prompt was created",
    pending: "No source records have arrived",
    next: `Send the prompt to ${catalogProviderName(generated.provider)}`,
  } : {
    label: "No source connected",
    tone: "empty",
    happened: "Your private workspace exists",
    pending: "No scan prompt or source sync yet",
    next: `Create a private ${selected.label} scan prompt`,
  };

  async function generatePrompt() {
    if (!personalOrganization || !canManage) return;
    setGenerating(true);
    setError("");
    try {
      const result = await api<{ key: FreshKey }>("/api/app/keys", {
        method: "POST",
        body: JSON.stringify({ name: `${catalogProviderName(provider)} catalog`, scopes: ["report"] }),
      }, personalOrganization.id);
      setGenerated({ provider, prompt: buildCatalogPrompt(provider, result.key.token) });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create the discovery prompt");
    } finally {
      setGenerating(false);
    }
  }

  async function importCatalog() {
    if (!personalOrganization) return;
    setImporting(true);
    setError("");
    try {
      const payload = extractJson(manualJson);
      await api("/api/app/catalog/sync", { method: "POST", body: JSON.stringify(payload) }, personalOrganization.id);
      setManualJson("");
      setManualOpen(false);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That inventory was not valid JSON");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="page catalog-onboarding journey-page journey-onboarding">
      <header className="catalog-hero">
        <p className="eyebrow"><Sparkles size={11} /> Sources · scan first, activate deliberately</p>
        <h1>Your agents already know<br />what you <em>loop.</em></h1>
        <p>Send one private scan prompt to each place you work with AI. LoopGroup assembles the recurring system around you without editing a loop or installing a schedule.</p>
        {!catalogLoading && !loadError && <p className="catalog-hero__facts" aria-live="polite">{formatCount(catalog.totals.sources, "source")} · {formatCount(catalog.totals.loops, "loop")} visible</p>}
      </header>

      <section className={`journey-ledger is-${journeyState.tone}`} aria-live="polite">
        <div><span className={`journey-state-chip is-${journeyState.tone}`}><Radio size={12} /> {journeyState.label}</span><strong>Catalog status</strong></div>
        <dl><div><dt>Already happened</dt><dd>{journeyState.happened}</dd></div><div><dt>Not happened</dt><dd>{journeyState.pending}</dd></div><div><dt>Exact next action</dt><dd>{journeyState.next}</dd></div></dl>
        {loadError && <Button variant="secondary" onClick={() => void load()}><RefreshCw size={14} /> Retry catalog</Button>}
      </section>
      {loadError && <div className="form-error journey-inline-error" role="alert"><strong>Catalog read failed.</strong><span>{loadError}</span><small>No source, reporting, or sharing state changed.</small></div>}

      <div className="catalog-progress catalog-progress--three" aria-label={`Onboarding step ${progress} of 3`}>
        {["Scan an agent", "Review the map", "Choose live reporting"].map((label, index) => <span className={progress >= index + 1 ? "is-active" : ""} key={label}><i>{progress > index + 1 ? <Check size={11} /> : `0${index + 1}`}</i>{label}</span>)}
      </div>

      <section className="catalog-stage">
        <div className="catalog-compose">
          <div className="catalog-provider-grid">
            {providers.map((item) => {
              const source = catalog.sources.find((candidate) => candidate.provider === item.id);
              return <button className={`${provider === item.id ? "is-selected" : ""} ${source ? "is-connected" : ""}`} key={item.id} onClick={() => setProvider(item.id)}><ProviderMark provider={item.id} size={32} /><b>{item.label}</b><small>{source ? `${source.loopCount} cataloged` : item.detail}</small>{source && <Check size={13} />}</button>;
            })}
          </div>

          <div className="catalog-prompt-card">
            <div className="catalog-prompt-card__head"><span><Bot size={15} /> SCAN PROMPT → {selected.label.toUpperCase()}</span><span className="catalog-scope"><LockKeyhole size={12} /> report-only key</span></div>
            {promptMatchesSelection ? (
              <>
                <pre>{generated.prompt}</pre>
                <div className="catalog-prompt-card__actions"><CopyButton value={generated.prompt} label={`Copy prompt for ${selected.label}`} /><Button variant="ghost" onClick={() => setGenerated(null)}>Start over</Button></div>
              </>
            ) : (
              <div className="catalog-prompt-empty">
                <ScanSearch size={29} />
                <h2>Ask {selected.label} to map what it can substantiate.</h2>
                <p>The scoped prompt authorizes one metadata sync. It explicitly forbids loop edits, recurring schedules, and invented run outcomes.</p>
                {canManage ? <Button onClick={generatePrompt} busy={generating}><Sparkles size={15} /> Generate private scan prompt</Button> : <p className="catalog-admin-note">An owner or admin needs to generate the scoped prompt.</p>}
              </div>
            )}
          </div>
          <p className="catalog-plugin-note">Prefer a plugin? <Link to="/app/settings#keys">Install LoopGroup for Claude Code or Codex</Link> and run the same scan as a slash command.</p>

          <div className="catalog-boundary">
            <span><Eye size={16} /><b>What crosses the boundary</b><small>Names, purpose, cadence, triggers, status, tags, and evidence level.</small></span>
            <span><LockKeyhole size={16} /><b>What stays with the agent</b><small>Secrets, prompt contents, conversations, documents, customer data, and outputs.</small></span>
          </div>
        </div>

        <aside className={`catalog-listener ${catalog.totals.sources ? "catalog-listener--received" : ""}`}>
          <div className="catalog-listener__head"><div><p className="eyebrow">Live receiver</p><h2>{catalog.totals.sources ? "The map is forming" : "Listening for a signal"}</h2></div><button onClick={() => void load()} className="icon-button" aria-label="Refresh catalog"><RefreshCw size={15} /></button></div>
          {catalogLoading ? (
            <div className="catalog-radar is-loading" role="status"><i /><i /><i /><span><RefreshCw className="spin" size={18} /></span><p>Reading your private catalog</p><small>Source, loop, and sharing state remain unconfirmed until this finishes.</small></div>
          ) : !catalog.totals.sources ? (
            <div className="catalog-radar"><i /><i /><i /><span><Radio size={18} /></span><p>{generated ? `Waiting for ${catalogProviderName(generated.provider)}…` : "No agent connected yet"}</p><small>Keep this page open or return later. Sync happens through Cloudflare, not this browser tab.</small></div>
          ) : (
            <>
              <div className="catalog-totals"><span><strong>{catalog.totals.loops}</strong><small>loops found</small></span><span><strong>{catalog.totals.sources}</strong><small>agent {catalog.totals.sources === 1 ? "source" : "sources"}</small></span></div>
              <div className="catalog-loop-list">{latestLoops.map((loop, index) => <Link to={`/app/loops/${loop.id}`} key={loop.id}><span className="catalog-loop-list__index">{String(index + 1).padStart(2, "0")}</span><span><b>{loop.name}</b><small>{loop.source.name} · {loop.cadence || "cadence unknown"}</small></span><i className={`confidence confidence--${loop.confidence}`}>{loop.confidence || "unrated"}</i></Link>)}</div>
              <div className="catalog-next"><p><strong>One source is useful. Several reveal the system.</strong><br />Send a separate prompt to each agent you use; LoopGroup keeps their provenance distinct.</p></div>
            </>
          )}
        </aside>
      </section>

      <section className="catalog-fallback">
        <div><ClipboardPaste size={19} /><span><b>Agent cannot call MCP or HTTPS?</b><small>Ask it to return the JSON inventory, then paste it here. The result lands in the same catalog.</small></span></div>
        <Button variant="secondary" onClick={() => setManualOpen((value) => !value)}>{manualOpen ? "Close importer" : "Paste returned JSON"}</Button>
        {manualOpen && <div className="catalog-import"><textarea value={manualJson} onChange={(event) => setManualJson(event.target.value)} placeholder={'{\n  "source": { "provider": "claude", "name": "Claude" },\n  "loops": [...]\n}'} /><Button onClick={importCatalog} busy={importing} disabled={!manualJson.trim()}><Send size={14} /> Import catalog</Button></div>}
      </section>

      {catalog.totals.sources > 0 && <section className="activation-stage" id="activation">
        <header>
          <div><p className="eyebrow"><Radio size={11} /> After the scan</p><h2>Make visibility live—on your terms.</h2><p>The catalog is already useful. If you want it to stay current or show real outcomes, send a second prompt that previews every proposed change and waits for your approval.</p></div>
          <span><LockKeyhole size={14} /> Nothing changes until you approve</span>
        </header>
        <div className="activation-stage__choices">
          <article><span><Eye size={17} /></span><div><h3>Catalog observer</h3><p>Rediscovers names, schedules, and new loops. It keeps the map current but never invents a run outcome.</p><small>Environment level · one routine · exact location and removal steps shown first</small></div></article>
          <article><span><Workflow size={17} /></span><div><h3>Direct run reporting</h3><p>Adds non-blocking start and finish signals so success, failure, duration, and reported cost can become visible.</p><small>Selected loops only · per-loop approval · exact edits and rollback shown first</small></div></article>
        </div>
        {activationOpen ? <div className="activation-stage__prompt">
          <div><span><Bot size={14} /> APPROVAL-PLANNING PROMPT FOR {catalogProviderName(activationProvider).toUpperCase()}</span><button className="icon-button" onClick={() => setActivationOpen(false)} aria-label="Close activation prompt">×</button></div>
          <pre>{activationPrompt}</pre>
          <footer><CopyButton value={activationPrompt} label="Copy activation planning prompt" /><small>Send this to the already-connected {activationSource?.name || catalogProviderName(activationProvider)} environment.</small></footer>
        </div> : <div className="activation-stage__action"><div><FileSearch size={18} /><span><b>Preview before install</b><small>The agent will return proposed observer and per-loop changes, then ask what you approve.</small></span></div><Button variant="secondary" onClick={() => setActivationOpen(true)}>Create activation plan <ArrowRight size={14} /></Button></div>}
      </section>}

      {catalog.totals.sources > 0 && <section className="categorize-stage" id="categorize" aria-labelledby="categorize-title">
        <header>
          <div><p className="eyebrow"><Tag size={11} aria-hidden="true" /> Tags</p><h2 id="categorize-title">Categorize my loops</h2><p>Ask {catalogProviderName(activationProvider)} to propose a small vocabulary of category tags across the catalog and apply it only after you approve. Tags are what the Sharing page filters and rules run on; LoopGroup organizes by them but never guesses one.</p></div>
          <span><LockKeyhole size={13} aria-hidden="true" /> The agent proposes, you approve</span>
        </header>
        <div className="categorize-stage__body">
          <div className="categorize-stage__actions"><CopyButton value={categorizePrompt} label="Copy categorize prompt" variant="primary" /><Button variant="ghost" onClick={() => setCategorizeOpen((value) => !value)} aria-expanded={categorizeOpen}>{categorizeOpen ? "Hide prompt" : "Show prompt"}</Button></div>
          {categorizeOpen && <pre>{categorizePrompt}</pre>}
        </div>
        <footer><LockKeyhole size={14} aria-hidden="true" /><span>The agent reads the loops it already knows, shows you the proposed tags with the loops under each, and applies exactly what you approve with tag_loops. It never edits a loop, its schedule, or anything outside LoopGroup.</span></footer>
      </section>}

      <section className="catalog-handoff">
        <div><p className="eyebrow"><Share2 size={12} /> After activation</p><h2>Share the loops worth sharing.</h2><p>Grants live on the Sharing page: one loop to one group, reversible, and nothing crosses without one.</p></div>
        <Link className="button button--secondary" to="/app/people">Open Sharing <ArrowRight size={14} /></Link>
      </section>

      {error && <div className="form-error journey-inline-error" role="alert"><strong>That action did not finish.</strong><span>{error}</span><small>The last confirmed catalog state remains shown.</small></div>}
      <footer className="catalog-finish"><span>{providerSource ? <><Check size={15} /> {providerSource.name} synced {formatRelative(providerSource.lastSyncedAt)}</> : "Cataloging is additive. A partial view never deletes an existing loop."}</span><Link className="button button--primary" to="/app">Open Pulse <ArrowRight size={15} /></Link></footer>
    </div>
  );
}
